import { FormEvent, useCallback, useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { supabase } from "../lib/supabase";
import type { Profile } from "../lib/types";

export function AdminPage() {
  const { session } = useAuth();
  const [users, setUsers] = useState<Profile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    username: "",
    email: "",
    password: "",
    display_name: "",
    is_admin: false,
  });
  const [reset, setReset] = useState({ userId: "", password: "" });

  const load = useCallback(async () => {
    const { data, error: err } = await supabase
      .from("jh_profiles")
      .select("id, username, display_name, is_admin")
      .order("username");
    if (err) setError(err.message);
    else setUsers((data ?? []) as Profile[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function callAdmin(path: string, body: Record<string, unknown>) {
    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${path}`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json.error || json.message || `HTTP ${res.status}`);
    return json;
  }

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await callAdmin("admin-users", {
        action: "create",
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
        display_name: form.display_name.trim() || form.username.trim(),
        is_admin: form.is_admin,
      });
      setMessage(`Created user ${form.username}`);
      setForm({ username: "", email: "", password: "", display_name: "", is_admin: false });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onReset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await callAdmin("admin-users", {
        action: "reset_password",
        user_id: reset.userId,
        password: reset.password,
      });
      setMessage("Password updated.");
      setReset({ userId: "", password: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack-gap">
      <div className="panel">
        <h2 style={{ marginTop: 0 }}>Admin · Users</h2>
        <p className="muted">Add users and reset passwords. First account <code>khardy</code> is admin.</p>
        {error ? <p className="error">{error}</p> : null}
        {message ? <p className="muted">{message}</p> : null}

        <div className="table-wrap" style={{ marginBottom: 18 }}>
          <table className="data">
            <thead>
              <tr>
                <th>Username</th>
                <th>Display name</th>
                <th>Admin</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>{u.display_name || "—"}</td>
                  <td>{u.is_admin ? "Yes" : "No"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3>Add user</h3>
        <form className="stack-gap" onSubmit={onCreate}>
          <div className="field">
            <label>Username</label>
            <input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              required
            />
          </div>
          <div className="field">
            <label>Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
          </div>
          <div className="field">
            <label>Temporary password</label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              minLength={8}
            />
          </div>
          <div className="field">
            <label>Display name</label>
            <input
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            />
          </div>
          <label>
            <input
              type="checkbox"
              checked={form.is_admin}
              onChange={(e) => setForm({ ...form, is_admin: e.target.checked })}
            />{" "}
            Admin
          </label>
          <button className="btn primary" type="submit" disabled={busy}>
            Create user
          </button>
        </form>
      </div>

      <div className="panel">
        <h3 style={{ marginTop: 0 }}>Reset password</h3>
        <form className="stack-gap" onSubmit={onReset}>
          <div className="field">
            <label>User</label>
            <select
              value={reset.userId}
              onChange={(e) => setReset({ ...reset, userId: e.target.value })}
              required
            >
              <option value="">Select…</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>New password</label>
            <input
              type="password"
              value={reset.password}
              onChange={(e) => setReset({ ...reset, password: e.target.value })}
              required
              minLength={8}
            />
          </div>
          <button className="btn" type="submit" disabled={busy}>
            Reset password
          </button>
        </form>
      </div>
    </div>
  );
}
